import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import FileManager from '../components/FileManager';

const AdminDashboard = ({ isPreAuthenticated = false, preAuthCredentials = null, onLogout = null }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(isPreAuthenticated);
  const [credentials, setCredentials] = useState(preAuthCredentials || { username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState({ statues: [], lightings: [] });
  const [activeTab, setActiveTab] = useState('statues');
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedProducts, setExpandedProducts] = useState({});
  const [expandedInventory, setExpandedInventory] = useState({});
  const [inventoryState, setInventoryState] = useState({});
  const navigate = useNavigate();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Check authentication on mount
  useEffect(() => {
    if (isPreAuthenticated && preAuthCredentials) {
      setCredentials(preAuthCredentials);
      fetchProducts(preAuthCredentials);
    } else {
      const savedAuth = sessionStorage.getItem('adminAuth');
      if (savedAuth) {
        const auth = JSON.parse(savedAuth);
        setCredentials(auth);
        setIsAuthenticated(true);
        fetchProducts(auth);
      }
    }
  }, [isPreAuthenticated, preAuthCredentials]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${backendUrl}/api/admin/verify`, {
        method: 'GET',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminAuth', JSON.stringify(credentials));
        fetchProducts(credentials);
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (auth) => {
    setLoading(true);
    try {
      // Fetch statues
      const statuesRes = await fetch(`${backendUrl}/api/admin/products/statues`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${auth.username}:${auth.password}`)
        }
      });
      
      // Fetch lightings
      const lightingsRes = await fetch(`${backendUrl}/api/admin/products/lightings`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${auth.username}:${auth.password}`)
        }
      });

      if (statuesRes.ok && lightingsRes.ok) {
        const statuesData = await statuesRes.json();
        const lightingsData = await lightingsRes.json();
        
        setProducts({
          statues: statuesData,
          lightings: lightingsData,
          total_statues: statuesData.length,
          total_lightings: lightingsData.length
        });
      } else {
        setError('Failed to fetch products');
      }
    } catch (err) {
      setError('Error loading products: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const removeFirstImage = async (productId, productName, type) => {
    if (!window.confirm(`Remove the first image from "${productName}"?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const endpoint = type === 'statues' 
        ? `/api/admin/products/statues/${productId}/remove-first-image`
        : `/api/admin/products/lightings/${productId}/remove-first-image`;

      const response = await fetch(`${backendUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(`✅ ${result.message}`);
        // Refresh products
        fetchProducts(credentials);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to remove image');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateImages = async (productId, currentImages, productName, type) => {
    const newImagesStr = window.prompt(
      `Update images for "${productName}"\nEnter image paths separated by commas:`,
      currentImages.join(', ')
    );

    if (!newImagesStr) return;

    const newImages = newImagesStr.split(',').map(img => img.trim()).filter(img => img);

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const endpoint = type === 'statues'
        ? `/api/admin/products/statues/${productId}/images`
        : `/api/admin/products/lightings/${productId}/images`;

      const response = await fetch(`${backendUrl}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ images: newImages })
      });

      if (response.ok) {
        setSuccessMessage(`✅ Images updated for ${productName}`);
        fetchProducts(credentials);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update images');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (productId, productName, currentImages, type) => {
    // Create file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.multiple = true;
    
    fileInput.onchange = async (e) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      
      setLoading(true);
      setError('');
      setSuccessMessage('');
      
      try {
        const uploadedUrls = [];
        
        // Upload each file
        for (let file of files) {
          const formData = new FormData();
          formData.append('file', file);
          
          const response = await fetch(`${backendUrl}/api/admin/upload-image`, {
            method: 'POST',
            headers: {
              'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
            },
            body: formData
          });
          
          if (response.ok) {
            const result = await response.json();
            uploadedUrls.push(result.url);
          } else {
            const errorData = await response.json();
            setError(`Failed to upload ${file.name}: ${errorData.detail}`);
          }
        }
        
        if (uploadedUrls.length > 0) {
          // Add new images to existing images
          const newImages = [...currentImages, ...uploadedUrls];
          
          // Update product with new images
          const endpoint = type === 'statues'
            ? `/api/admin/products/statues/${productId}/images`
            : `/api/admin/products/lightings/${productId}/images`;
          
          const updateResponse = await fetch(`${backendUrl}${endpoint}`, {
            method: 'PUT',
            headers: {
              'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ images: newImages })
          });
          
          if (updateResponse.ok) {
            setSuccessMessage(`✅ Added ${uploadedUrls.length} image(s) to ${productName}. Note: If new images don't appear on the website, do a hard refresh (Ctrl+Shift+R or Cmd+Shift+R) to clear browser cache.`);
            // Force refetch with cache bypass
            fetchProducts(credentials);
          } else {
            setError('Failed to update product images');
          }
        }
      } catch (err) {
        setError('Upload error: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fileInput.click();
  };

  const toggleColorVariantSold = async (productId, color, currentStatus, type) => {
    if (!window.confirm(`Mark color "${color}" as ${currentStatus ? 'available' : 'sold'}?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const endpoint = type === 'statues'
        ? `/api/admin/products/statues/${productId}/color-variant/toggle-sold`
        : `/api/admin/products/lightings/${productId}/color-variant/toggle-sold`;

      const response = await fetch(`${backendUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          color: color,
          sold: !currentStatus
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(`✅ ${result.message}`);
        // Refresh products
        fetchProducts(credentials);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update color variant');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleProductExpansion = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const toggleInventoryExpansion = (productId) => {
    setExpandedInventory(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const handleInventoryChange = (productId, variantName, value) => {
    setInventoryState(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [variantName]: parseInt(value) || 0
      }
    }));
  };

  const saveInventory = async (productId, productName, type) => {
    if (!window.confirm(`Save inventory changes for "${productName}"?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const collection = type === 'statues' ? 'statues' : 'lightings';
      const variants = inventoryState[productId];

      if (!variants || Object.keys(variants).length === 0) {
        setError('No inventory changes to save');
        return;
      }

      const variantUpdates = Object.entries(variants).map(([variantName, stock]) => ({
        variant_name: variantName,
        stock: stock
      }));

      const response = await fetch(
        `${backendUrl}/api/admin/products/${collection}/inventory/${productId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ variants: variantUpdates })
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(`✅ ${result.message}`);
        setInventoryState(prev => {
          const newState = { ...prev };
          delete newState[productId];
          return newState;
        });
        fetchProducts(credentials);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update inventory');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleVariantSoldManual = async (productId, variantName, productName, type) => {
    if (!window.confirm(`Toggle sold status for variant "${variantName}" of "${productName}"?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const collection = type === 'statues' ? 'statues' : 'lightings';

      const response = await fetch(
        `${backendUrl}/api/admin/products/${collection}/${productId}/variant/${variantName}/toggle-sold`,
        {
          method: 'POST',
          headers: {
            'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`),
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(`✅ ${result.message}`);
        fetchProducts(credentials);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to toggle sold status');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStockStatus = (stock, threshold = 5) => {
    if (stock === 0) return { label: '🔴 Out of Stock', color: 'red' };
    if (stock <= threshold) return { label: '🟡 Low Stock', color: 'yellow' };
    return { label: '🟢 In Stock', color: 'green' };
  };

  const hasInventoryChanges = (productId) => {
    return inventoryState[productId] && Object.keys(inventoryState[productId]).length > 0;
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      setIsAuthenticated(false);
      setCredentials({ username: '', password: '' });
      sessionStorage.removeItem('adminAuth');
      navigate('/');
    }
  };

  // Login Form
  if (!isAuthenticated && !isPreAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg shadow-2xl p-8 w-full max-w-md border border-gray-700">
          <h1 className="text-3xl font-bold text-white mb-6 text-center">Admin Dashboard</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-gray-300 mb-2">Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            {error && (
              <div className="bg-red-500 bg-opacity-20 border border-red-500 text-red-300 px-4 py-2 rounded">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          <button
            onClick={() => navigate('/')}
            className="w-full mt-4 text-gray-400 hover:text-white transition"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Main Dashboard
  const currentProducts = activeTab === 'statues' ? products.statues : activeTab === 'lightings' ? products.lightings : [];

  return (
    <div className={isPreAuthenticated ? "" : "min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white"}>
      {/* Header - Only show if not pre-authenticated */}
      {!isPreAuthenticated && (
        <div className="bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold">Image Management Dashboard</h1>
            <div className="flex gap-4">
              <button
                onClick={() => fetchProducts(credentials)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition"
              >
              🔄 Refresh
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition"
            >
              Logout
            </button>
          </div>
        </div>
        </div>
      )}

      <div className={`max-w-7xl mx-auto ${isPreAuthenticated ? 'p-4' : 'p-4'}`}>
        {/* Refresh Button - Always visible for pre-authenticated view */}
        {isPreAuthenticated && (
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => fetchProducts(credentials)}
              disabled={loading}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-50"
            >
              🔄 Refresh Products
            </button>
          </div>
        )}
        {/* Messages */}
        {successMessage && (
          <div className="bg-green-500 bg-opacity-20 border border-green-500 text-green-300 px-4 py-3 rounded-lg mb-4">
            {successMessage}
          </div>
        )}
        {error && (
          <div className="bg-red-500 bg-opacity-20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('statues')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'statues'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Statues ({products.total_statues || 0})
          </button>
          <button
            onClick={() => setActiveTab('lightings')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'lightings'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Lightings ({products.total_lightings || 0})
          </button>
          <button
            onClick={() => setActiveTab('files')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'files'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            File Manager
          </button>
        </div>

        {/* Loading State */}
        {loading && activeTab !== 'files' && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            <p className="mt-4 text-gray-400">Loading products...</p>
          </div>
        )}

        {/* File Manager */}
        {activeTab === 'files' && (
          <div className="h-[calc(100vh-300px)]">
            <FileManager credentials={credentials} backendUrl={backendUrl} />
          </div>
        )}

        {/* Products Grid */}
        {!loading && activeTab !== 'files' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentProducts.map((product) => (
              <div
                key={product.id}
                className="bg-gray-800 rounded-lg border border-gray-700 p-4 hover:border-blue-500 transition"
              >
                <h3 className="text-xl font-semibold mb-3 text-blue-400">{product.name}</h3>
                <p className="text-sm text-gray-400 mb-3">ID: {product.id}</p>

                {/* Color Variants Section */}
                {product.colorVariants && Object.keys(product.colorVariants).length > 0 && (
                  <div className="mb-4 bg-gray-900 rounded-lg border border-gray-600">
                    <button
                      onClick={() => toggleProductExpansion(product.id)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-700 transition rounded-lg"
                    >
                      <span className="text-sm font-semibold text-yellow-400">
                        🎨 Color Variants ({Object.keys(product.colorVariants).length})
                      </span>
                      {expandedProducts[product.id] ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    
                    {expandedProducts[product.id] && (
                      <div className="p-3 space-y-2 border-t border-gray-600">
                        {Object.entries(product.colorVariants).map(([colorName, colorData]) => {
                          const isSold = colorData.sold || false;
                          return (
                            <div
                              key={colorName}
                              className={`p-3 rounded-lg border ${
                                isSold 
                                  ? 'bg-red-900 bg-opacity-20 border-red-500' 
                                  : 'bg-green-900 bg-opacity-20 border-green-500'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-sm">{colorName}</span>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  isSold ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
                                }`}>
                                  {isSold ? '❌ SOLD' : '✅ Available'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400 mb-2">
                                <div>Price: {colorData.price}</div>
                                <div>Image Index: #{colorData.imageIndex}</div>
                              </div>
                              <button
                                onClick={() => toggleColorVariantSold(product.id, colorName, isSold, activeTab)}
                                className={`w-full px-3 py-2 rounded text-xs font-semibold transition ${
                                  isSold
                                    ? 'bg-green-600 hover:bg-green-700 text-white'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                }`}
                              >
                                {isSold ? 'Mark as Available' : 'Mark as Sold'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Inventory Management Section */}
                {(product.sizes || product.colorVariants) && (
                  <div className="mb-4 bg-gray-900 rounded-lg border border-purple-600">
                    <button
                      onClick={() => toggleInventoryExpansion(product.id)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-700 transition rounded-lg"
                    >
                      <span className="text-sm font-semibold text-purple-400">
                        📦 Inventory Management ({Object.keys(product.sizes || product.colorVariants || {}).length} variants)
                      </span>
                      {expandedInventory[product.id] ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    
                    {expandedInventory[product.id] && (
                      <div className="p-3 space-y-3 border-t border-gray-600">
                        {/* For products with sizes */}
                        {product.sizes && Object.entries(product.sizes).map(([sizeName, sizeData]) => {
                          const stock = sizeData.stock !== undefined ? sizeData.stock : 1; // Default to 1
                          const isSold = sizeData.sold || false;
                          const isManualOverride = sizeData.manualSoldOverride || false;
                          const stockStatus = getStockStatus(stock);
                          const currentValue = inventoryState[product.id]?.[sizeName] ?? stock;
                          
                          const borderColor = stockStatus.color === 'green' ? 'border-green-500 bg-green-900 bg-opacity-20' :
                                            stockStatus.color === 'yellow' ? 'border-yellow-500 bg-yellow-900 bg-opacity-20' :
                                            isSold && isManualOverride ? 'border-orange-500 bg-orange-900 bg-opacity-20' :
                                            'border-red-500 bg-red-900 bg-opacity-20';
                          
                          return (
                            <div key={sizeName} className={`p-3 rounded-lg border-2 ${borderColor}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-sm text-white">Size: {sizeName}</span>
                                <span className={`text-xs px-2 py-1 rounded font-semibold ${
                                  isSold && isManualOverride ? 'bg-orange-600 text-white' :
                                  isSold ? 'bg-red-600 text-white' :
                                  stockStatus.color === 'green' ? 'bg-green-600 text-white' :
                                  'bg-yellow-600 text-white'
                                }`}>
                                  {isSold && isManualOverride ? '🔴 SOLD (Manual)' : isSold ? '🔴 Out of Stock' : stockStatus.label}
                                </span>
                              </div>
                              
                              <div className="text-xs text-gray-400 mb-2">
                                Price: {sizeData.price}
                              </div>
                              
                              <div className="flex items-center gap-2 mb-2">
                                <label className="text-xs text-gray-300 font-medium">Stock:</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={currentValue}
                                  onChange={(e) => handleInventoryChange(product.id, sizeName, e.target.value)}
                                  className="w-20 px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 text-center text-sm font-semibold"
                                />
                                <span className="text-xs text-gray-400">units</span>
                              </div>
                              
                              {stock === 0 && !isManualOverride && (
                                <div className="mt-2 text-xs text-red-300 flex items-center gap-1 bg-red-900 bg-opacity-40 p-2 rounded border border-red-600">
                                  <span>🤖</span>
                                  <div>
                                    <div className="font-semibold">AUTO-SOLD (Stock = 0):</div>
                                    <div>✓ &quot;SOLD&quot; overlay + &quot;Add to Basket&quot; blocked</div>
                                  </div>
                                </div>
                              )}
                              
                              {stock > 0 && stock <= 5 && !isSold && (
                                <div className="mt-2 text-xs text-yellow-300 flex items-center gap-1 bg-yellow-900 bg-opacity-30 p-2 rounded">
                                  <span>⚠️</span>
                                  <span>Low stock alert will be sent when threshold is reached</span>
                                </div>
                              )}
                              
                              {isManualOverride && stock > 0 && (
                                <div className="mt-2 text-xs text-orange-300 flex items-center gap-1 bg-orange-900 bg-opacity-40 p-2 rounded border border-orange-600">
                                  <span>👤</span>
                                  <div>
                                    <div className="font-semibold">MANUAL SOLD:</div>
                                    <div>✓ &quot;SOLD&quot; overlay + &quot;Add to Basket&quot; blocked</div>
                                    <div className="text-gray-300 mt-1">Note: {stock} units in stock but hidden from customers</div>
                                  </div>
                                </div>
                              )}
                              
                              <button
                                onClick={() => toggleVariantSoldManual(product.id, sizeName, product.name, activeTab)}
                                disabled={stock === 0 && !isManualOverride}
                                className={`w-full mt-2 px-3 py-2 rounded text-xs font-semibold transition ${
                                  stock === 0 && !isManualOverride
                                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                                    : isSold
                                    ? 'bg-green-600 hover:bg-green-700 text-white'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                }`}
                              >
                                {stock === 0 && !isManualOverride 
                                  ? '✓ Already Marked as Sold (Auto)' 
                                  : isSold 
                                  ? '✅ Mark as Available (Remove Sold Status)' 
                                  : '🔴 Mark as Sold (Manual Override)'}
                              </button>
                            </div>
                          );
                        })}
                        
                        {/* For products with colorVariants */}
                        {!product.sizes && product.colorVariants && Object.entries(product.colorVariants).map(([colorName, colorData]) => {
                          const stock = colorData.stock !== undefined ? colorData.stock : 1; // Default to 1
                          const isSold = colorData.sold || false;
                          const isManualOverride = colorData.manualSoldOverride || false;
                          const stockStatus = getStockStatus(stock);
                          const currentValue = inventoryState[product.id]?.[colorName] ?? stock;
                          
                          const borderColor = stockStatus.color === 'green' ? 'border-green-500 bg-green-900 bg-opacity-20' :
                                            stockStatus.color === 'yellow' ? 'border-yellow-500 bg-yellow-900 bg-opacity-20' :
                                            isSold && isManualOverride ? 'border-orange-500 bg-orange-900 bg-opacity-20' :
                                            'border-red-500 bg-red-900 bg-opacity-20';
                          
                          return (
                            <div key={colorName} className={`p-3 rounded-lg border-2 ${borderColor}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-sm text-white">Color: {colorName}</span>
                                <span className={`text-xs px-2 py-1 rounded font-semibold ${
                                  isSold && isManualOverride ? 'bg-orange-600 text-white' :
                                  isSold ? 'bg-red-600 text-white' :
                                  stockStatus.color === 'green' ? 'bg-green-600 text-white' :
                                  'bg-yellow-600 text-white'
                                }`}>
                                  {isSold && isManualOverride ? '🔴 SOLD (Manual)' : isSold ? '🔴 Out of Stock' : stockStatus.label}
                                </span>
                              </div>
                              
                              <div className="text-xs text-gray-400 mb-2">
                                Price: {colorData.price}
                              </div>
                              
                              <div className="flex items-center gap-2 mb-2">
                                <label className="text-xs text-gray-300 font-medium">Stock:</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={currentValue}
                                  onChange={(e) => handleInventoryChange(product.id, colorName, e.target.value)}
                                  className="w-20 px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 text-center text-sm font-semibold"
                                />
                                <span className="text-xs text-gray-400">units</span>
                              </div>
                              
                              {stock === 0 && !isManualOverride && (
                                <div className="mt-2 text-xs text-red-300 flex items-center gap-1 bg-red-900 bg-opacity-40 p-2 rounded border border-red-600">
                                  <span>🤖</span>
                                  <div>
                                    <div className="font-semibold">AUTO-SOLD (Stock = 0):</div>
                                    <div>✓ &quot;SOLD&quot; overlay + &quot;Add to Basket&quot; blocked</div>
                                  </div>
                                </div>
                              )}
                              
                              {stock > 0 && stock <= 5 && !isSold && (
                                <div className="mt-2 text-xs text-yellow-300 flex items-center gap-1 bg-yellow-900 bg-opacity-30 p-2 rounded">
                                  <span>⚠️</span>
                                  <span>Low stock alert will be sent when threshold is reached</span>
                                </div>
                              )}
                              
                              {isManualOverride && stock > 0 && (
                                <div className="mt-2 text-xs text-orange-300 flex items-center gap-1 bg-orange-900 bg-opacity-40 p-2 rounded border border-orange-600">
                                  <span>👤</span>
                                  <div>
                                    <div className="font-semibold">MANUAL SOLD:</div>
                                    <div>✓ &quot;SOLD&quot; overlay + &quot;Add to Basket&quot; blocked</div>
                                    <div className="text-gray-300 mt-1">Note: {stock} units in stock but hidden from customers</div>
                                  </div>
                                </div>
                              )}
                              
                              <button
                                onClick={() => toggleVariantSoldManual(product.id, colorName, product.name, activeTab)}
                                disabled={stock === 0 && !isManualOverride}
                                className={`w-full mt-2 px-3 py-2 rounded text-xs font-semibold transition ${
                                  stock === 0 && !isManualOverride
                                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                                    : isSold
                                    ? 'bg-green-600 hover:bg-green-700 text-white'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                }`}
                              >
                                {stock === 0 && !isManualOverride 
                                  ? '✓ Already Marked as Sold (Auto)' 
                                  : isSold 
                                  ? '✅ Mark as Available (Remove Sold Status)' 
                                  : '🔴 Mark as Sold (Manual Override)'}
                              </button>
                            </div>
                          );
                        })}
                        
                        <button
                          onClick={() => saveInventory(product.id, product.name, activeTab)}
                          disabled={!hasInventoryChanges(product.id) || loading}
                          className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                        >
                          💾 Save Inventory Changes
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Images Display */}
                <div className="mb-4">
                  <p className="text-sm text-gray-400 mb-2">
                    Images ({product.images?.length || 0}):
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto bg-gray-900 p-2 rounded">
                    {product.images?.length > 0 ? (
                      product.images.map((img, idx) => (
                        <div
                          key={idx}
                          className={`text-xs p-2 rounded ${
                            img.includes('blob:')
                              ? 'bg-red-900 text-red-300 border border-red-500'
                              : 'bg-gray-700 text-gray-300'
                          }`}
                        >
                          <span className="font-semibold">#{idx + 1}:</span> {img}
                          {img.includes('blob:') && (
                            <span className="ml-2 text-red-400 font-bold">⚠️ BROKEN</span>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">No images</p>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => uploadImage(product.id, product.name, product.images || [], activeTab)}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition text-sm font-semibold"
                  >
                    📤 Upload Images
                  </button>
                  <button
                    onClick={() => removeFirstImage(product.id, product.name, activeTab)}
                    disabled={!product.images || product.images.length <= 1}
                    className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                  >
                    🗑️ Remove First Image
                  </button>
                  <button
                    onClick={() => updateImages(product.id, product.images || [], product.name, activeTab)}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition text-sm font-semibold"
                  >
                    ✏️ Edit All Images
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && currentProducts.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <p className="text-xl">No {activeTab} found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
